/**
 * Utility module: getGroups/matches.js.
 */

import { groups } from 'd3-array';

/** @typedef {import('./index').GroupMatches} GroupMatches */

/**
 * Pull the name of the round a match belongs to.
 * @param {{ matchInfo?: { series?: { name?: string }, stage?: { name?: string } } }} match
 * @returns {string}
 */
const getRoundName = (match) =>
  match?.matchInfo?.series?.name ?? match?.matchInfo?.stage?.name ?? '';

/**
 * Compare two matches by their ISO kickoff date, oldest first.
 *
 * Used to stably sort each group's match list. Falls back to `0` when a
 * date is missing so unparseable fixtures don't get pushed to NaN
 * positions in the sort.
 *
 * @param {{ matchInfo?: unknown }} a
 * @param {{ matchInfo?: unknown }} b
 * @returns {number}
 */
const byKickoffAsc = (a, b) => {
  const da = a?.isoDate ? new Date(a.isoDate).getTime() || 0 : 0;
  const db = b?.isoDate ? new Date(b.isoDate).getTime() || 0 : 0;
  return da - db;
};

/**
 * Bucket a tournament's matches by group-stage group, ordered chronologically.
 *
 * Walks `matches.matches.match`, groups entries by their round name
 * (preferring `series.name` over `stage.name`), keeps only buckets whose
 * name contains `'Group'`, and returns them sorted alphabetically by
 * group name with each bucket's matches sorted by kickoff time.
 *
 * The function is pure: it does not mutate `matches`.
 *
 * @param {{
 *   matches?: {
 *     match?: Array<{
 *       matchInfo?: {
 *         series?: { name?: string },
 *         stage?: { name?: string }
 *       }
 *     }>
 *   }
 * } | null | undefined} matches
 *   The matches payload (typically `data.matches`).
 * @returns {GroupMatches[]}
 *   One entry per group-stage group, e.g.
 *   `[{ groupName: 'Group A', matches: [...] }, ...]`. Returns an empty
 *   array when no matches are available.
 */
export default (matches) => {
  const allMatches = matches?.matches?.match;
  if (!Array.isArray(allMatches) || allMatches.length === 0) return [];

  // `d3-array.groups` returns `[key, values][]`; we want only group-stage
  // rounds, then sort the round names alphabetically for stable rendering.
  const groupRounds = groups(allMatches, getRoundName)
    .filter(([roundName]) => roundName.includes('Group'))
    .sort(([a], [b]) => a.localeCompare(b));

  return groupRounds.map(([groupName, groupMatches]) => ({
    groupName,
    matches: [...groupMatches].sort(byKickoffAsc),
  }));
};
